import { useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import portalDataService from '../../services/portalDataService';
import usePortalList from '../hooks/usePortalList';
import PortalFilterBar from '../components/PortalFilterBar';
import PortalStatusBadge from '../components/PortalStatusBadge';
import PortalPurchaseViewModal from '../components/PortalPurchaseViewModal';
import { formatDate, formatMoney } from '../portalUtils';
import { UI_COLORS } from '../../utils/colors';

// Purchase Orders tab = what the customer BUYS from the company (company sales
// orders). Order-shaped: order_number, delivery_location, `units` (plural).
const ALL_STATUSES = ['pending', 'confirmed', 'delivery_assigned', 'loading', 'loaded', 'in_transit', 'delivered', 'invoiced', 'cancelled'];
const PAGE_SIZE = 20;

const PortalPurchasesPage = () => {
  const { t } = useTranslation();
  const s = t('portal.purchases', { returnObjects: true });
  const c = t('portal.common', { returnObjects: true });
  const rawStatusLabels = t('portal.purchases.statuses', { returnObjects: true });
  const statusLabels = (rawStatusLabels && typeof rawStatusLabels === 'object') ? rawStatusLabels : {};

  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetcher = useCallback((params) => portalDataService.getPurchases(params), []);
  const filters = useMemo(() => ({ status, from_date: fromDate, to_date: toDate }), [status, fromDate, toDate]);
  const { items, total, totalPages, loading, error, reload } = usePortalList(fetcher, filters, page, PAGE_SIZE);

  const rows = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((o) =>
      (o.order_number || '').toLowerCase().includes(q) ||
      (o.delivery_location_name || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const statusOptions = useMemo(
    () => [{ value: '', label: c?.allStatuses || 'All statuses' }, ...ALL_STATUSES.map((v) => ({ value: v, label: statusLabels[v] || v }))],
    [c, statusLabels]
  );

  const detailReqRef = useRef(0);
  const openDetail = useCallback(async (row) => {
    const reqId = ++detailReqRef.current;
    setSelected(row);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await portalDataService.getPurchase(row.id);
      if (reqId !== detailReqRef.current) return;
      // On a failed/404 detail fetch show the modal's empty state, not the stale
      // list row dressed up as the real detail.
      setDetail(res.success ? res.data : null);
    } catch (err) {
      console.error('Failed to load purchase order detail:', err);
      if (reqId === detailReqRef.current) setDetail(null);  // show empty state, not the stale list row
    } finally {
      // A superseded request must not clear the flag the newer one owns.
      if (reqId === detailReqRef.current) setDetailLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    { id: 'order_number', label: s?.columns?.orderNumber || 'Order No.', renderCell: (o) => <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{o.order_number || '—'}</span> },
    { id: 'date', label: s?.columns?.date || 'Date', renderCell: (o) => formatDate(o.date) },
    { id: 'delivery_location_name', label: s?.columns?.deliveryLocation || 'Delivery Location', renderCell: (o) => o.delivery_location_name || '—' },
    { id: 'total_amount', label: s?.columns?.total || 'Total', align: 'right', renderCell: (o) => formatMoney(o.total_amount) },
    { id: 'status', label: s?.columns?.status || 'Status', renderCell: (o) => <PortalStatusBadge status={o.status} labels={statusLabels} /> },
    { id: 'actions', label: c?.actions || 'Actions', align: 'center', renderCell: (o) => (
      <button type="button" onClick={() => openDetail(o)} className="px-3 py-1 rounded-lg text-xs font-semibold" style={{ color: UI_COLORS.BRAND.TERTIARY, backgroundColor: 'rgba(24,154,180,0.1)' }}>
        {c?.view || 'View'}
      </button>
    ) },
  ], [s, c, statusLabels, openDetail]);

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{s?.title || 'Purchase Orders'}</h1>

      <PortalFilterBar
        statusOptions={statusOptions}
        status={status}
        onStatusChange={(v) => { setStatus(v); setPage(1); }}
        showDates
        fromDate={fromDate} toDate={toDate}
        onFromDateChange={(v) => { setFromDate(v); setPage(1); }}
        onToDateChange={(v) => { setToDate(v); setPage(1); }}
        search={search} onSearchChange={setSearch}
      />

      {error && !loading && (
        // A failed load previously fell through to the "No purchases" empty state,
        // so the customer couldn't tell a server error from genuinely-empty data.
        <div role="alert" style={{ background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <span>{typeof error === 'string' ? error : 'Could not load your purchases. Please try again.'}</span>
          <button onClick={() => reload()} style={{ fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer' }}>Retry</button>
        </div>
      )}
      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        keyField="id"
        enablePagination={false}
        emptyState={{ title: s?.empty?.title || 'No purchases', message: s?.empty?.message || 'Orders you place will appear here.' }}
      />

      {!search.trim() && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalCount={total}
          onPageChange={setPage}
          loading={loading}
          totalLabel={s?.totalLabel || 'total orders'}
          pageSize={PAGE_SIZE}
        />
      )}

      <PortalPurchaseViewModal isOpen={!!selected} onClose={() => setSelected(null)} order={detail} loading={detailLoading} statusLabels={statusLabels} />
    </div>
  );
};

export default PortalPurchasesPage;
